import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, Webhook } from 'lucide-react';
import { api } from '@/api/client';

interface WebhookRow {
  merchantId: number;
  url: string;
  enabled?: boolean;
  note?: string;
}

export function MerchantWebhooksPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'merchant_webhooks'],
    queryFn: async () => {
      try { return (await api.get<WebhookRow[]>('/settings/merchant_webhooks')).data ?? []; }
      catch { return [] as WebhookRow[]; }
    },
  });

  const [rows, setRows] = useState<WebhookRow[]>([]);
  useEffect(() => { if (data) setRows(Array.isArray(data) ? data : []); }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put('/settings/merchant_webhooks', rows)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'merchant_webhooks'] }); toast.success('Webhooks saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  function update(idx: number, field: keyof WebhookRow, value: any) {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: field === 'merchantId' ? Number(value) : value };
      return next;
    });
  }
  function addRow() { setRows((prev) => [...prev, { merchantId: 0, url: '', enabled: true }]); }
  function removeRow(i: number) { setRows((prev) => prev.filter((_, idx) => idx !== i)); }

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Webhook size={18} /> Merchant Webhooks</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            When a tracking event lands for any of these merchants, we POST the order &amp; status payload to their URL.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={addRow} className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-1.5">
            <Plus size={14} /> Add Webhook
          </button>
          <button onClick={() => save.mutate()} disabled={save.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save All
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5 w-32">Merchant ID</th>
              <th className="text-left px-4 py-2.5">Webhook URL</th>
              <th className="text-left px-4 py-2.5 w-44">Note</th>
              <th className="text-center px-4 py-2.5 w-24">Enabled</th>
              <th className="px-4 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No webhooks configured. Click "Add Webhook" to start.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-4 py-2">
                  <input type="number" value={r.merchantId || ''} onChange={(e) => update(i, 'merchantId', e.target.value)}
                    className="input h-9 text-sm w-24 font-mono" />
                </td>
                <td className="px-4 py-2">
                  <input value={r.url} onChange={(e) => update(i, 'url', e.target.value)}
                    className="input h-9 text-sm w-full font-mono" placeholder="https://merchant.example.com/api/courier-webhook" />
                </td>
                <td className="px-4 py-2">
                  <input value={r.note ?? ''} onChange={(e) => update(i, 'note', e.target.value)}
                    className="input h-9 text-sm w-full" placeholder="e.g. Ananda Live" />
                </td>
                <td className="px-4 py-2 text-center">
                  <input type="checkbox" checked={r.enabled !== false} onChange={(e) => update(i, 'enabled', e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300" />
                </td>
                <td className="px-4 py-2">
                  <button onClick={() => removeRow(i)} className="text-rose-500 hover:text-rose-700">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <strong>Note:</strong> URLs must be HTTPS and respond with HTTP 200. Retries are attempted 3 times with exponential backoff on 4xx/5xx.
      </div>
    </div>
  );
}
